import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./AuthContext";
import Layout from "./layout/Layout";
import LoginPage from "./admin/LoginPage";
import ProtectRoute from "./hooks/ProtectRoute";
import ScrollToTop from "./components/ScrollToTop";
import PrivacyPolicy from "./pages/privacypolicy";
import FloatingDonateButton from "./components/FloatingDonateButton";
import FloatingLanguageButton from "./components/FloatingLanguageButton"; // ⬅️ NEW

// 🚀 Razorpay connection warm-up (optional perf)
import { preconnectRazorpay, prewarmRazorpayOnIdle } from "./utils/razorpay";

// Lazy-loaded pages
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Donations = lazy(() => import("./pages/Donations"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Stories = lazy(() => import("./pages/Stories"));
const MembershipForm = lazy(() => import("./pages/Member"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin
const Admin = lazy(() => import("./admin/Home"));
const AdminDonation = lazy(() => import("./admin/Donation"));
const AdminNewsletter = lazy(() => import("./admin/NewsLetters"));
const AdminGallery = lazy(() => import("./admin/Gallery"));
const RequestResetPassword = lazy(() => import("./admin/RequestPass"));
const ResetPassword = lazy(() => import("./admin/ResetPassword"));

export default function App() {
  // Warm the network + idle-load checkout.js (only on good networks)
  useEffect(() => {
    try {
      preconnectRazorpay();
      const conn = navigator?.connection;
      const saveData = conn?.saveData === true;
      const fast = !conn?.effectiveType || conn.effectiveType === "4g";
      if (!saveData && fast) {
        prewarmRazorpayOnIdle(); // non-blocking, safe to skip on slow/data-saver
      }
    } catch {}
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop behavior="auto" />
      <AuthProvider>
        {/* Toast Notifications */}
        <Toaster position="top-center" />

        <Suspense
          fallback={
            <div className="fixed inset-0 flex justify-center items-center bg-white/70 z-[9999]">
              <CircularProgress aria-label="Loading page" />
            </div>
          }
        >
          <Routes>
            {/* Public pages */}
            <Route path="/" element={<Layout wrappedComponents={<Home />} />} />
            <Route
              path="/about-shivanshmahasabha"
              element={<Layout wrappedComponents={<About />} />}
            />
            <Route
              path="/donations/make-a-change"
              element={<Layout wrappedComponents={<Donations />} />}
            />
            <Route path="/gallery" element={<Layout wrappedComponents={<Gallery />} />} />
            <Route path="/stories" element={<Layout wrappedComponents={<Stories />} />} />
            <Route path="/membership" element={<Layout wrappedComponents={<MembershipForm />} />} />
            <Route
              path="/privacy-policy"
              element={<Layout wrappedComponents={<PrivacyPolicy />} />}
            />

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/request-reset-password" element={<RequestResetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />

            {/* Admin (protected) */}
            <Route element={<ProtectRoute />}>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/donations" element={<AdminDonation />} />
              <Route path="/admin/newsletters" element={<AdminNewsletter />} />
              <Route path="/admin/gallery" element={<AdminGallery />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>

          {/* Floating buttons, visible on all routes */}
          <FloatingLanguageButton />   {/* ⬅️ NEW */}
          <FloatingDonateButton />
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
